// ============================================================================
// SPARQL QUERY BUILDERS FOR INTELLISPOT ONTOLOGY
// ============================================================================

import type { OntologyDomain, SparqlResults } from '@/types/ontology'
import { query } from '@/lib/api/sparql-adapter'
import { NAMESPACES } from '@/lib/ontology/namespaces'

// ----------------------------------------------------------------------------
// CLASS QUERIES
// ----------------------------------------------------------------------------

/**
 * Get all classes in the ontology
 */
export async function getAllClasses(): Promise<SparqlResults> {
  return query(`
    SELECT DISTINCT ?class ?label ?comment
    WHERE {
      ?class a owl:Class .
      OPTIONAL { ?class rdfs:label ?label }
      OPTIONAL { ?class rdfs:comment ?comment }
      FILTER(!isBlank(?class))
    }
    ORDER BY ?class
  `)
}

/**
 * Get all subclasses of a class
 */
export async function getSubClasses(classUri: string): Promise<SparqlResults> {
  return query(`
    SELECT DISTINCT ?subclass ?label
    WHERE {
      ?subclass rdfs:subClassOf <${classUri}> .
      OPTIONAL { ?subclass rdfs:label ?label }
    }
    ORDER BY ?subclass
  `)
}

/**
 * Get all properties of a class (including inherited)
 */
export async function getClassProperties(classUri: string): Promise<SparqlResults> {
  return query(`
    SELECT DISTINCT ?property ?label ?range ?propertyType
    WHERE {
      {
        ?property rdfs:domain <${classUri}> .
      } UNION {
        <${classUri}> rdfs:subClassOf* ?superClass .
        ?property rdfs:domain ?superClass .
      }
      OPTIONAL { ?property rdfs:label ?label }
      OPTIONAL { ?property rdfs:range ?range }
      BIND(
        IF(EXISTS { ?property a owl:ObjectProperty }, "object",
        IF(EXISTS { ?property a owl:DatatypeProperty }, "data", "unknown"))
        AS ?propertyType
      )
    }
    ORDER BY ?property
  `)
}

// ----------------------------------------------------------------------------
// INSTANCE QUERIES
// ----------------------------------------------------------------------------

/**
 * Get all instances of a class
 * For Facet instances, also fetches io:content and io:title as fallback labels
 */
export async function getInstancesOfClass(classUri: string, limit = 100, offset = 0): Promise<SparqlResults> {
  return query(`
    SELECT ?instance (SAMPLE(?lbl) AS ?label) (SAMPLE(?cmt) AS ?comment) (SAMPLE(?cnt) AS ?content) (SAMPLE(?ttl) AS ?title)
    WHERE {
      ?instance a <${classUri}> .
      OPTIONAL { ?instance rdfs:label ?lbl }
      OPTIONAL { ?instance rdfs:comment ?cmt }
      OPTIONAL { ?instance <${NAMESPACES.io}content> ?cnt }
      OPTIONAL { ?instance <${NAMESPACES.io}title> ?ttl }
      OPTIONAL { ?instance <${NAMESPACES.io}status> ?st }
      FILTER(!BOUND(?st) || ?st != "merged")
      FILTER NOT EXISTS { ?instance <${NAMESPACES.io}mergedInto> ?anyMerge }
    }
    GROUP BY ?instance
    ORDER BY ?instance
    LIMIT ${limit}
    OFFSET ${offset}
  `)
}

/**
 * Get instance count for a class
 */
export async function getInstanceCount(classUri: string): Promise<number> {
  const results = await query(`
    SELECT (COUNT(DISTINCT ?instance) AS ?count)
    WHERE {
      ?instance a <${classUri}> .
      OPTIONAL { ?instance <${NAMESPACES.io}status> ?st }
      FILTER(!BOUND(?st) || ?st != "merged")
      FILTER NOT EXISTS { ?instance <${NAMESPACES.io}mergedInto> ?anyMerge }
    }
  `)
  return parseInt(results.results.bindings[0]?.count?.value ?? '0', 10)
}

/**
 * Get instance counts for multiple classes in a single batched query.
 * Uses UNION to count all classes at once, dramatically reducing query count.
 */
export async function getBatchedInstanceCounts(classUris: string[]): Promise<Record<string, number>> {
  if (classUris.length === 0) {
    return {}
  }

  // Build a single query with UNION clauses for each class
  // Using VALUES clause for efficiency
  const results = await query(`
    SELECT ?type (COUNT(DISTINCT ?instance) AS ?count)
    WHERE {
      VALUES ?type { ${classUris.map((uri) => `<${uri}>`).join(' ')} }
      ?instance a ?type .
      OPTIONAL { ?instance <${NAMESPACES.io}status> ?st }
      FILTER(!BOUND(?st) || ?st != "merged")
      FILTER NOT EXISTS { ?instance <${NAMESPACES.io}mergedInto> ?anyMerge }
    }
    GROUP BY ?type
  `)

  // Convert results to a map
  const counts: Record<string, number> = {}

  // Initialize all classes with 0 (in case some have no instances)
  classUris.forEach((uri) => {
    counts[uri] = 0
  })

  // Fill in actual counts from query results
  results.results.bindings.forEach((binding) => {
    const typeUri = binding.type?.value
    const count = parseInt(binding.count?.value ?? '0', 10)
    if (typeUri) {
      counts[typeUri] = count
    }
  })

  return counts
}

/**
 * Get all properties of an instance
 */
export async function getInstanceProperties(instanceUri: string): Promise<SparqlResults> {
  return query(`
    SELECT ?property ?value ?valueLabel
    WHERE {
      <${instanceUri}> ?property ?value .
      OPTIONAL { ?value rdfs:label ?valueLabel }
      FILTER(?property != rdf:type)
    }
    ORDER BY ?property
  `)
}

/**
 * Get the type(s) of an instance
 */
export async function getInstanceTypes(instanceUri: string): Promise<SparqlResults> {
  return query(`
    SELECT DISTINCT ?type ?label
    WHERE {
      <${instanceUri}> a ?type .
      OPTIONAL { ?type rdfs:label ?label }
      FILTER(!isBlank(?type))
    }
    ORDER BY ?type
  `)
}

/**
 * Search instances by label
 */
export async function searchInstances(searchTerm: string, limit = 50): Promise<SparqlResults> {
  return query(`
    SELECT DISTINCT ?instance ?type ?label
    WHERE {
      ?instance a ?type .
      ?instance rdfs:label ?label .
      FILTER(CONTAINS(LCASE(?label), LCASE("${searchTerm}")))
      FILTER(!isBlank(?type))
    }
    ORDER BY ?label
    LIMIT ${limit}
  `)
}

// ----------------------------------------------------------------------------
// RELATIONSHIP QUERIES
// ----------------------------------------------------------------------------

/**
 * Get all outgoing relationships from an instance
 */
export async function getOutgoingRelationships(instanceUri: string): Promise<SparqlResults> {
  return query(`
    SELECT ?property ?target ?targetLabel ?targetType
    WHERE {
      <${instanceUri}> ?property ?target .
      ?target a ?targetType .
      OPTIONAL { ?target rdfs:label ?targetLabel }
      FILTER(isIRI(?target))
      FILTER(?property != rdf:type)
    }
    ORDER BY ?property
  `)
}

/**
 * Get all incoming relationships to an instance
 */
export async function getIncomingRelationships(instanceUri: string): Promise<SparqlResults> {
  return query(`
    SELECT ?source ?sourceLabel ?sourceType ?property
    WHERE {
      ?source ?property <${instanceUri}> .
      ?source a ?sourceType .
      OPTIONAL { ?source rdfs:label ?sourceLabel }
      FILTER(isIRI(?source))
    }
    ORDER BY ?property
  `)
}

// ----------------------------------------------------------------------------
// CROSS-DOMAIN TRACEABILITY QUERIES
// ----------------------------------------------------------------------------

/**
 * Get the full traceability chain from a market signal to features
 */
export async function getTraceabilityChain(startUri: string): Promise<SparqlResults> {
  // Using namespace prefixes from SPARQL_PREFIXES
  return query(`
    SELECT ?step1 ?step2 ?step3 ?step4 ?step5 ?step6
           ?step1Label ?step2Label ?step3Label ?step4Label ?step5Label ?step6Label
    WHERE {
      OPTIONAL {
        <${startUri}> market:indicatesTrend ?step1 .
        OPTIONAL { ?step1 rdfs:label ?step1Label }
      }
      OPTIONAL {
        ?step1 ^business:linkedToTrend ?step2 .
        OPTIONAL { ?step2 rdfs:label ?step2Label }
      }
      OPTIONAL {
        ?step2 ^business:operationalizedBy ?step3 .
        OPTIONAL { ?step3 rdfs:label ?step3Label }
      }
      OPTIONAL {
        ?step3 solution:contains ?step4 .
        OPTIONAL { ?step4 rdfs:label ?step4Label }
      }
      OPTIONAL {
        ?step4 solution:delivers ?step5 .
        OPTIONAL { ?step5 rdfs:label ?step5Label }
      }
      OPTIONAL {
        ?step5 solution:mitigates ?step6 .
        OPTIONAL { ?step6 rdfs:label ?step6Label }
      }
    }
  `)
}

/**
 * Get impact analysis - what features are affected by a competitor move
 */
export async function getCompetitorImpact(competitorUri: string): Promise<SparqlResults> {
  return query(`
    SELECT DISTINCT ?feature ?featureLabel ?valueProp ?valuePropLabel
    WHERE {
      <${competitorUri}> market:exertsPressure ?force .
      ?threat business:linkedToCompetitor <${competitorUri}> .

      # Find value propositions that might be threatened
      ?valueProp a business:ValueProposition .
      OPTIONAL { ?valueProp rdfs:label ?valuePropLabel }

      # Find features that realize these value propositions
      ?product solution:implements ?valueProp .
      ?product solution:hasFeature ?feature .
      OPTIONAL { ?feature rdfs:label ?featureLabel }
    }
  `)
}

/**
 * Get all instances by domain
 */
export async function getInstancesByDomain(domain: OntologyDomain, limit = 100): Promise<SparqlResults> {
  const namespaceUri = NAMESPACES[domain]

  return query(`
    SELECT DISTINCT ?instance ?type ?label
    WHERE {
      ?instance a ?type .
      FILTER(STRSTARTS(STR(?type), "${namespaceUri}"))
      OPTIONAL { ?instance rdfs:label ?label }
    }
    ORDER BY ?type ?label
    LIMIT ${limit}
  `)
}

// ----------------------------------------------------------------------------
// GRAPH VISUALIZATION QUERIES
// ----------------------------------------------------------------------------

/**
 * Get all instances with their relationships for graph visualization
 */
export async function getGraphData(limit = 200): Promise<SparqlResults> {
  return query(`
    SELECT DISTINCT ?subject ?subjectLabel ?subjectType ?predicate ?predicateLabel ?object ?objectLabel ?objectType
    WHERE {
      ?subject a ?subjectType .
      ?subject ?predicate ?object .
      ?object a ?objectType .
      FILTER(isIRI(?object))
      FILTER(?predicate != rdf:type)
      OPTIONAL { ?subject rdfs:label ?subjectLabel }
      OPTIONAL { ?predicate rdfs:label ?predicateLabel }
      OPTIONAL { ?object rdfs:label ?objectLabel }
    }
    LIMIT ${limit}
  `)
}

/**
 * Get all instances (nodes) for graph
 */
export async function getAllInstances(limit = 100): Promise<SparqlResults> {
  return query(`
    SELECT DISTINCT ?instance ?label ?type
    WHERE {
      ?instance a ?type .
      FILTER(!isBlank(?type))
      FILTER(!STRSTARTS(STR(?type), "http://www.w3.org/"))
      OPTIONAL { ?instance rdfs:label ?label }
    }
    ORDER BY ?type ?label
    LIMIT ${limit}
  `)
}

// ----------------------------------------------------------------------------
// STATISTICS QUERIES
// ----------------------------------------------------------------------------

/**
 * Get ontology statistics
 * Optimized to fetch all domain counts in a single query
 */
export async function getOntologyStats(): Promise<{
  totalTriples: number
  totalClasses: number
  totalInstances: number
  instancesByDomain: Record<OntologyDomain, number>
}> {
  // Run all queries in parallel (4 queries instead of 7)
  const [triplesResult, classesResult, instancesResult, domainCountsResult] = await Promise.all([
    query('SELECT (COUNT(*) AS ?count) WHERE { ?s ?p ?o }'),
    query('SELECT (COUNT(DISTINCT ?c) AS ?count) WHERE { ?c a owl:Class }'),
    query('SELECT (COUNT(DISTINCT ?i) AS ?count) WHERE { ?i a ?type . ?type a owl:Class }'),
    // Single query to count instances by domain using GROUP BY
    query(`
      SELECT ?domain (COUNT(DISTINCT ?i) AS ?count)
      WHERE {
        ?i a ?type .
        BIND(
          IF(STRSTARTS(STR(?type), "${NAMESPACES.core}"), "core",
          IF(STRSTARTS(STR(?type), "${NAMESPACES.market}"), "market",
          IF(STRSTARTS(STR(?type), "${NAMESPACES.business}"), "business",
          IF(STRSTARTS(STR(?type), "${NAMESPACES.solution}"), "solution",
          "other")))) AS ?domain
        )
        FILTER(?domain != "other")
      }
      GROUP BY ?domain
    `),
  ])

  // Parse domain counts from grouped result
  const domainCounts: Record<OntologyDomain, number> = {
    core: 0,
    market: 0,
    business: 0,
    solution: 0,
  }

  domainCountsResult.results.bindings.forEach((binding) => {
    const domain = binding.domain?.value as OntologyDomain | undefined
    if (domain && domain in domainCounts) {
      domainCounts[domain] = parseInt(binding.count?.value ?? '0', 10)
    }
  })

  return {
    totalTriples: parseInt(triplesResult.results.bindings[0]?.count?.value ?? '0', 10),
    totalClasses: parseInt(classesResult.results.bindings[0]?.count?.value ?? '0', 10),
    totalInstances: parseInt(instancesResult.results.bindings[0]?.count?.value ?? '0', 10),
    instancesByDomain: domainCounts,
  }
}
